import { useReadExternalConnection } from '@/hooks/externalConnectionsApi';
import { useToast } from '@/hooks/use-toast';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button, buttonVariants } from '@/components/ui/button';
import ExternalConnectionFiltersAlt from '../../ExternalConnections/Filters/ExternalConnectionFilters';
import { FieldValues } from 'react-hook-form';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  IntegrationLogLevel,
} from '@/lib/integrationTypes/integrationTypes';
import { IntegrationScriptLogDto } from '@/lib/api/types.gen';

type NewExternalConnectionDeviceProps = {
  id: string;
  border?: boolean;
  onChange: (values: FieldValues) => void;
  onSubmit?: (value: FieldValues) => void;
  values: FieldValues;
  processing: boolean;
  existingDevice?: boolean;
  logs?: IntegrationScriptLogDto[] | null;
};

const NewExternalConnectionDevice = ({
  id,
  border,
  onChange,
  onSubmit,
  processing,
  values,
  existingDevice,
  logs,
}: NewExternalConnectionDeviceProps) => {
  const { data: externalConnectionResponse, error, isLoading } = useReadExternalConnection({ id });
  const { toast } = useToast();

  const externalConnection = externalConnectionResponse?.externalConnection;

  useEffect(() => {
    if (error) {
      toast({
        title: 'Could not load device',
        description: error.message,
        variant: 'destructive',
      });
    }
  }, [error, toast]);

  if (isLoading) {
    return (
      <section
        className={`mt-6 rounded-xl ${border === undefined || border === true ? 'border' : ''} bg-[#111]`}
      >
        <div className="p-6">
          <h2 className="text-xl font-semibold">About this Device</h2>
          <p className="text-gray-400">Loading external connection options</p>
        </div>
      </section>
    );
  }

  if (externalConnection!.deviceCreationKeyValues) {
    <section
      className={`mt-6 rounded-xl ${border === undefined || border === true ? 'border' : ''} bg-[#111]`}
    >
      <div className="p-6">
        <p>Please re-index the VMS connection</p>
        <Link className={buttonVariants()} to={`/connection/${externalConnection!.id!}`}>
          Goto Connection Edit
        </Link>
      </div>
    </section>;
  }

  return (
    <>
      <section
        className={`mt-6 rounded-xl ${border === undefined || border === true ? 'border' : ''} bg-card2`}
      >
        <div className="p-6">
          <h2 className="text-xl font-semibold">
            {existingDevice
              ? `External Connection - ${externalConnection!.name}`
              : `Add from ${externalConnection!.name}`}
          </h2>

          <ExternalConnectionFiltersAlt
            disabled={existingDevice}
            externalConnection={externalConnection!}
            onChange={onChange}
            onSubmit={onSubmit}
            values={values}
            processing={processing}
          />
          <p className="pt-4 text-gray">
            {existingDevice && 'To change the connection details, please recreate the device.'}
          </p>
        </div>

        {logs ? (
          <div className="mt-2 p-6">
            <Collapsible>
              <CollapsibleTrigger asChild>
                <Button>Logs</Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="mt-3 flex flex-col gap-2">
                  {logs.map((log, index) => (
                    <div key={index}>
                      <hr className="mb-1 w-full bg-gray-500" />
                      <div className="flex flex-row items-center gap-3">
                        <div
                          className={
                            'rounded-sm px-2 py-1 ' +
                            (log.level === IntegrationLogLevel.Info
                              ? 'bg-blue-800'
                              : log.level === IntegrationLogLevel.Warning
                                ? 'bg-yellow-800'
                                : log.level === IntegrationLogLevel.Error
                                  ? 'bg-red-800'
                                  : 'bg-gray-800')
                          }
                        >
                          {log.level === IntegrationLogLevel.Info
                            ? 'Info'
                            : log.level === IntegrationLogLevel.Warning
                              ? 'Warning'
                              : log.level === IntegrationLogLevel.Error
                                ? 'Error'
                                : 'Debug'}{' '}
                        </div>
                        <p>{log.message}</p>
                      </div>
                      <hr className="mt-1 w-full bg-gray-500" />
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        ) : undefined}
      </section>
    </>
  );
};

export default NewExternalConnectionDevice;
